import * as React from 'react';
import { backButtonText } from 'owa-locstrings/lib/strings/backbuttontext.locstring.json';
import { showToolButtonText } from 'owa-locstrings/lib/strings/showtoolbarbuttontext.locstring.json';
import { hideToolButtonText } from 'owa-locstrings/lib/strings/hidetoolbarbuttontext.locstring.json';
import { observer } from 'mobx-react-lite';
import loc, { isCurrentCultureRightToLeft } from 'owa-localize';
import { Icon } from '@fluentui/react/lib/Icon';
import { ControlIcons } from 'owa-control-icons';
import { clearSelectedAssets, getSelectedAssets, showProjectsToolbox } from '../viewstate';
import classNames from 'classnames';
import TimeStreamAssetSubTools from './TimeStreamAssetSubTools';
import TimeStreamAssetTools from './TimeStreamAssetTools';
import useProjectsToolbox from '../hooks/useProjectsToolbox';
import { updateSpacesOptions } from 'owa-timestream-options';
import useFirstRunExperience from '../hooks/useFirstRunExperience';
import {
    shouldShowExpandedToolbox,
    collapseToolbox,
    expandToolbox,
    commonStyles,
} from 'owa-timestream-toolbox-common';

export default observer(function TimeStreamToolsToolbox() {
    const { LazyTimeStreamProjectsToolboxHeaderButton } = useProjectsToolbox();
    const { ProjectsCallout } = useFirstRunExperience();
    const headerButtonRef = React.useRef<HTMLButtonElement>(null);
    const toolboxExpanded = shouldShowExpandedToolbox();
    const isRTL = isCurrentCultureRightToLeft();

    const collapsedToolboxIcon = isRTL
        ? ControlIcons.DoubleChevronLeft
        : ControlIcons.DoubleChevronRight;
    const expandedToolboxIcon = isRTL
        ? ControlIcons.DoubleChevronRight
        : ControlIcons.DoubleChevronLeft;

    const hasSelectedAssets = getSelectedAssets().length > 0;

    const genericHeaderButtonText = hasSelectedAssets
        ? loc(backButtonText)
        : toolboxExpanded
        ? loc(hideToolButtonText)
        : loc(showToolButtonText);
    const genericHeaderButtonOnClick = hasSelectedAssets
        ? clearSelectedAssets
        : toolboxExpanded
        ? onCollapse
        : onExpand;

    const headerButtonClass = classNames(commonStyles.toolContainer, commonStyles.header, {
        [commonStyles.useThemeColor]: hasSelectedAssets,
        [commonStyles.rootHeader]: !hasSelectedAssets,
    });

    React.useEffect(() => {
        // when component mounts for the first time, should start with focus
        headerButtonRef.current?.focus();
    }, []);

    return (
        <>
            {LazyTimeStreamProjectsToolboxHeaderButton ? (
                <LazyTimeStreamProjectsToolboxHeaderButton
                    hasSelectedAssets={hasSelectedAssets}
                    clearSelectedAssets={clearSelectedAssets}
                    headerButtonRef={headerButtonRef}
                    headerButtonClass={headerButtonClass}
                    showProjectsToolbox={showProjectsToolbox}
                />
            ) : (
                <button
                    className={headerButtonClass}
                    title={genericHeaderButtonText}
                    onClick={genericHeaderButtonOnClick}
                    ref={headerButtonRef}>
                    {hasSelectedAssets && (
                        <>
                            <div className={commonStyles.toolIcon}>
                                <Icon iconName={isRTL ? ControlIcons.Forward : ControlIcons.Back} />
                            </div>
                            {toolboxExpanded && (
                                <div className={commonStyles.toolTitle}>{loc(backButtonText)}</div>
                            )}
                        </>
                    )}
                </button>
            )}
            {hasSelectedAssets ? <TimeStreamAssetSubTools /> : <TimeStreamAssetTools />}
            <button
                className={classNames(commonStyles.toolContainer, commonStyles.toolboxFooter)}
                title={toolboxExpanded ? loc(hideToolButtonText) : loc(showToolButtonText)}
                onClick={toolboxExpanded ? onCollapse : onExpand}>
                <div className={commonStyles.toolIcon}>
                    <Icon iconName={toolboxExpanded ? expandedToolboxIcon : collapsedToolboxIcon} />
                </div>
                {toolboxExpanded && (
                    <div className={commonStyles.toolTitle}>{loc(hideToolButtonText)}</div>
                )}
            </button>
            {ProjectsCallout && <ProjectsCallout target={headerButtonRef} />}
        </>
    );
});

function onExpand() {
    expandToolbox();
    updateSpacesOptions({ toolboxCollapsed: false });
}

function onCollapse() {
    collapseToolbox();
    updateSpacesOptions({ toolboxCollapsed: true });
}
